import { queryOptions } from '@tanstack/react-query';
import type { Attachment } from '@rundown/types';
import { api } from '../../lib/api';
import { queryKeys } from '../../lib/queryKeys';

/**
 * Attachment transport.
 *
 *   POST /workspaces/:ws/attachments/presign  -> { uploadUrl, fields, objectKey, maxBytes }
 *   POST <uploadUrl>                          -> the bytes, straight to the store
 *   POST /workspaces/:ws/attachments          -> the recorded row
 *   GET  /workspaces/:ws/requests/:id/attachments
 *
 * The middle step does not go through `api.ts`: it is a cross-origin POST to
 * MinIO or S3 and must carry no Authorization header, no CSRF header and no
 * cookies. Sending them would fail the preflight against a service that never
 * asked for them.
 */

export interface PresignedUpload {
  readonly objectKey: string;
  readonly uploadUrl: string;
  readonly fields: Record<string, string>;
  readonly maxBytes: number;
}

function basePath(workspaceId: string): string {
  return `/workspaces/${encodeURIComponent(workspaceId)}/attachments`;
}

export function presignUpload(
  workspaceId: string,
  input: { filename: string; contentType: string; byteSize: number },
): Promise<PresignedUpload> {
  return api.post<PresignedUpload>(`${basePath(workspaceId)}/presign`, input);
}

export function registerAttachment(
  workspaceId: string,
  input: {
    objectKey: string;
    filename: string;
    contentType: string;
    requestId?: string | null;
    messageId?: string | null;
  },
): Promise<Attachment> {
  return api.post<Attachment>(basePath(workspaceId), input);
}

/**
 * Send the bytes.
 *
 * The policy fields must be appended **before** the file part — S3 reads the
 * form in order and ignores anything after `file`, so a field added afterwards
 * is silently dropped and the signature fails for no visible reason.
 *
 * XMLHttpRequest rather than fetch, only because fetch still cannot report
 * upload progress. A 40 MB screen recording with no progress bar looks frozen.
 */
export function uploadToStore(
  presigned: PresignedUpload,
  file: File,
  onProgress?: (fraction: number) => void,
  signal?: AbortSignal,
): Promise<void> {
  const form = new FormData();
  for (const [key, value] of Object.entries(presigned.fields)) form.append(key, value);
  form.append('file', file);

  return new Promise<void>((resolve, reject) => {
    const request = new XMLHttpRequest();
    request.open('POST', presigned.uploadUrl, true);

    request.upload.addEventListener('progress', (event) => {
      if (event.lengthComputable) onProgress?.(event.loaded / event.total);
    });

    request.addEventListener('load', () => {
      // 204 from MinIO, 204 or 201 from S3.
      if (request.status >= 200 && request.status < 300) {
        resolve();
        return;
      }
      // The store answers in XML. EntityTooLarge is the one worth naming,
      // because it is the size cap doing its job rather than a fault.
      reject(
        new Error(
          /EntityTooLarge/.test(request.responseText)
            ? 'That file is too large to attach.'
            : `The upload was refused (${request.status}).`,
        ),
      );
    });

    request.addEventListener('error', () => reject(new Error('The upload failed. Check your connection.')));
    request.addEventListener('abort', () => reject(new DOMException('Aborted', 'AbortError')));

    signal?.addEventListener('abort', () => request.abort(), { once: true });
    request.send(form);
  });
}

export function attachmentsQueryOptions(workspaceId: string, requestId: string) {
  return queryOptions({
    queryKey: queryKeys.requestAttachments(workspaceId, requestId),
    queryFn: ({ signal }) =>
      api.get<{ items: Attachment[] }>(
        `/workspaces/${encodeURIComponent(workspaceId)}/requests/${encodeURIComponent(requestId)}/attachments`,
        signal,
      ),
    // Every `url` in the response is a signed link that expires in ten minutes.
    // Holding the list longer than that would leave the page rendering images
    // against URLs the store has already stopped honouring.
    staleTime: 5 * 60_000,
  });
}
